// Admin Home — "Journal Pipeline", built to the canonical design (design repo:
// docs/designs/2026-08-19-admin-homepage/, Claude Design "AE View: Landing Page ReDesign";
// WO-2026-08-19-admin-homepage). Third and last role landing page: where the AE page answers
// "what is waiting on me", this one answers "how much is in flight, and how is each country
// team moving it".
//
// Same four roster KPIs the Central page shows (GET /dashboard/staff's numbers, recomputed by
// GET /dashboard/admin from the identical rows), then the body regroups by country team —
// worst first — with a per-team "Needs attention" table instead of the AE-by-AE roster.
// Central keeps StaffDashboardView; only Admin lands here.
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { clsx } from "clsx";
import { preprintStatusLabel, type AdminPipeline, type AdminAttentionRow, type AdminTeamRow } from "@rrid/shared-types";
import { api } from "../../lib/api.js";
import { qk } from "../../lib/queryKeys.js";
import { flagFor } from "../../lib/teams.js";
import { Badge, Card, CardBody, EmptyState, Spinner, statusTone } from "../../components/ui.js";
import { KpiCard } from "./dashboard-ui.js";
import { Dot, HEALTH_LABEL, relativeAge, type Health } from "../admin/health.js";

const PUBLIC_SITE_HREF = "https://rrid.mitpress.mit.edu";

/** GET /api/admin/status, narrowed to what the header chip needs. */
interface StatusChip {
  overall: Health;
  checkedAt: string;
}

/** Section tone by rank: the worst team reads red, the next amber, the rest neutral —
 *  the design's only ordering cue besides the order itself. */
function toneFor(team: AdminTeamRow, index: number): "red" | "amber" | "slate" {
  if (team.stalled30 === 0 && team.uncheckedBacklog === 0) return "slate";
  if (index === 0) return "red";
  if (index === 1) return "amber";
  return "slate";
}

const SECTION_TONE: Record<"red" | "amber" | "slate", { card: string; head: string; dot: string }> = {
  red: { card: "border-red-200", head: "bg-red-50 border-red-200", dot: "bg-red-500" },
  amber: { card: "border-amber-200", head: "bg-amber-50 border-amber-200", dot: "bg-amber-500" },
  slate: { card: "border-slate-200", head: "bg-slate-50 border-slate-200", dot: "bg-slate-400" },
};

/** Metric value colour: red past the alarm threshold, amber past the watch threshold. */
function metricTone(value: number, watch: number, alarm: number): string {
  if (value >= alarm) return "text-red-700";
  if (value >= watch) return "text-amber-700";
  return "text-slate-800";
}

function Metric({ label, value, className }: { label: string; value: number; className?: string }): JSX.Element {
  return (
    <span className="block">
      <span className="block text-[11px] text-slate-500">{label}</span>
      <span className={clsx("mt-0.5 block text-xl font-bold tabular-nums", className ?? "text-slate-800")}>{value}</span>
    </span>
  );
}

/** The stacked in-flight bar: To Pitch(Editorial) / Active / On hold / Dormant. */
function PipelineBar({ data }: { data: AdminPipeline }): JSX.Element {
  const s = data.totals.byStatus;
  const legend: { label: string; count: number; bar: string; swatch: string }[] = [
    { label: preprintStatusLabel("To Pitch(Editorial)"), count: s.toPitchEditorial, bar: "bg-violet-400", swatch: "bg-violet-400" },
    { label: preprintStatusLabel("ACTIVE"), count: s.active, bar: "bg-blue-500", swatch: "bg-blue-500" },
    { label: preprintStatusLabel("ON HOLD"), count: s.onHold, bar: "bg-amber-500", swatch: "bg-amber-500" },
    { label: preprintStatusLabel("DORMANT"), count: s.dormant, bar: "bg-slate-300", swatch: "bg-slate-300" },
  ];
  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <div className="flex items-baseline gap-2.5">
            <span className="text-3xl font-bold tabular-nums">{data.totals.inProgress}</span>
            <span className="text-sm text-slate-500">
              preprints in progress across {data.totals.teamCount} {data.totals.teamCount === 1 ? "team" : "teams"}
            </span>
          </div>
          <span className={clsx("text-xs", data.totals.stalled30 > 0 ? "text-amber-700" : "text-slate-400")}>
            {data.totals.stalled30} with no movement in 30+ days
          </span>
        </div>
        <div className="flex h-2.5 gap-[3px] overflow-hidden rounded-full">
          {legend.map((l) => (
            <span key={l.label} className={clsx(l.bar)} style={{ flexGrow: l.count, flexBasis: 0 }} />
          ))}
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-1.5">
          {legend.map((l) => (
            <span key={l.label} className="inline-flex items-center gap-1.5 text-xs text-slate-600">
              <span className={clsx("inline-block h-2 w-2 rounded-sm", l.swatch)} />
              {l.label} <strong className="font-semibold tabular-nums text-slate-900">{l.count}</strong>
            </span>
          ))}
        </div>
      </CardBody>
    </Card>
  );
}

function AttentionTable({ rows, total, cap }: { rows: AdminAttentionRow[]; total: number; cap: number }): JSX.Element {
  if (rows.length === 0) {
    return <p className="px-4 py-3 text-xs text-slate-400">Nothing stalled or parked on this team.</p>;
  }
  return (
    <div>
      <div className="grid grid-cols-[minmax(0,1fr)_130px_172px_80px_92px] gap-3 border-b border-slate-200 bg-slate-50 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        <span>Needs attention</span>
        <span>Lead editor</span>
        <span>Status</span>
        <span className="text-right">Unchecked</span>
        <span className="text-right">In stage</span>
      </div>
      {rows.map((r) => (
        <div
          key={r.preprintId ?? r.selectedId ?? r.title}
          className="grid grid-cols-[minmax(0,1fr)_130px_172px_80px_92px] items-center gap-3 border-b border-slate-100 px-4 py-2.5 last:border-b-0 hover:bg-slate-50"
        >
          {r.preprintId ? (
            <Link to={`/preprints/${r.preprintId}`} className="min-w-0 text-sm font-semibold text-slate-900 hover:text-brand">
              {r.title}
            </Link>
          ) : (
            <span className="min-w-0 text-sm font-semibold text-slate-900">{r.title}</span>
          )}
          <span className="truncate text-xs text-slate-600" title={r.leadEditorEmail ?? undefined}>
            {r.leadEditorName ?? r.leadEditorEmail ?? "—"}
          </span>
          <span>
            <Badge tone={statusTone(r.status)}>{preprintStatusLabel(r.status)}</Badge>
          </span>
          <span
            className={clsx(
              "text-right text-sm tabular-nums",
              r.uncheckedCount == null ? "text-slate-300" : r.uncheckedCount > 0 ? "font-semibold text-amber-700" : "text-slate-600",
            )}
          >
            {r.uncheckedCount == null ? "—" : r.uncheckedCount}
          </span>
          <span className="text-right text-xs tabular-nums text-slate-500">
            {r.inStageDays == null ? "—" : `${r.inStageDays} days`}
          </span>
        </div>
      ))}
      {total > rows.length && (
        <p className="px-4 py-2 text-[11px] text-slate-400">
          Showing the {cap} stalest of {total} — open the team for the rest.
        </p>
      )}
    </div>
  );
}

function TeamSection({ team, index }: { team: AdminTeamRow; index: number }): JSX.Element {
  const tone = SECTION_TONE[toneFor(team, index)];
  // Short forms of the same vocabulary the status badges below use — "on hold" / "dormant" were
  // renamed app-wide in Stefano's Aug-2026 pass, so the chips can't keep the export's wording.
  const chips: { label: string; tone: "violet" | "blue" | "amber" | "slate" }[] = [
    { label: `${team.byStatus.toPitchEditorial} to pitch`, tone: "violet" },
    { label: `${team.byStatus.active} active`, tone: "blue" },
    { label: `${team.byStatus.onHold} pending reviewers`, tone: "amber" },
    { label: `${team.byStatus.dormant} in queue`, tone: "slate" },
  ];
  return (
    <section className={clsx("overflow-hidden rounded-lg border bg-white shadow-sm", tone.card)}>
      <div className={clsx("flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3", tone.head)}>
        <div className="flex min-w-0 flex-wrap items-center gap-2.5">
          <span className={clsx("inline-block h-2.5 w-2.5 shrink-0 rounded-full", tone.dot)} />
          <h3 className="text-[15px] font-bold text-slate-900">
            {flagFor(team.team)} {team.team}
          </h3>
          <span className="text-xs text-slate-500">
            {team.editorCount} {team.editorCount === 1 ? "editor" : "editors"}
          </span>
        </div>
        {/* The Productivity overview is already grouped by team — it IS the team view. */}
        <Link to="/productivity" className="shrink-0 text-xs font-medium text-brand hover:underline">
          Open team →
        </Link>
      </div>
      <div className="grid grid-cols-2 gap-3.5 border-b border-slate-100 px-4 py-3.5 sm:grid-cols-3 lg:grid-cols-5">
        <Metric label="In progress" value={team.inProgress} />
        <Metric label="Greenlits this week" value={team.greenlitsThisWeek} className="text-green-700" />
        <Metric label="Proposals this week" value={team.proposalsThisWeek} />
        <Metric label="Unchecked backlog" value={team.uncheckedBacklog} className={metricTone(team.uncheckedBacklog, 10, 25)} />
        <Metric label="Stalled 30+ days" value={team.stalled30} className={metricTone(team.stalled30, 1, 4)} />
      </div>
      <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-100 px-4 py-2.5">
        {chips.map((c) => (
          <Badge key={c.label} tone={c.tone}>
            {c.label}
          </Badge>
        ))}
      </div>
      <AttentionTable rows={team.needsAttention} total={team.attentionTotal} cap={5} />
    </section>
  );
}

export function AdminHomeView(): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: qk.dashboardAdmin,
    queryFn: () => api.get<AdminPipeline>("/dashboard/admin"),
  });
  // The health chip is best-effort: a failing status board must not blank the pipeline page.
  const { data: status } = useQuery({
    queryKey: qk.adminStatus,
    queryFn: () => api.get<StatusChip>("/admin/status"),
    retry: false,
  });

  if (isLoading || !data) return <Spinner label="Loading the journal pipeline…" />;

  const health: Health = status?.overall ?? "unknown";

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Journal Pipeline</h1>
          <p className="mt-0.5 text-sm text-slate-500">How much is in flight, and how each country team is moving it.</p>
        </div>
        <Link to="/admin" className="flex items-center gap-2 text-xs text-slate-400 hover:text-slate-600">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
            <Dot health={health} />
            System {HEALTH_LABEL[health].toLowerCase()}
          </span>
          <span>Checked {status ? relativeAge(status.checkedAt) : "—"}</span>
        </Link>
      </header>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Assistant Editors" value={data.kpis.aeCount} tone="amber" sublabel="Managers in roster" />
        <KpiCard label="Greenlits this week" value={data.kpis.greenlitsThisWeek} tone="green" sublabel="Across all teams" />
        <KpiCard label="Active preprints" value={data.kpis.activePreprints} tone="blue" sublabel="Being shepherded" />
        {/* The design labels this "Proposals this week"; the roster metric is to-date, so the
            sublabel says so rather than mislabelling the number (WO Track A). */}
        <KpiCard label="Proposals" value={data.kpis.proposals} tone="violet" sublabel="To date, on AE-led preprints" />
      </div>

      <PipelineBar data={data} />

      <div className="flex items-baseline gap-2.5 pt-1">
        <h2 className="shrink-0 text-xs font-bold uppercase tracking-[0.09em] text-slate-600">Team health</h2>
        <span className="h-px flex-1 bg-slate-200" />
        <span className="shrink-0 text-xs text-slate-400">Worst first</span>
      </div>

      {data.teams.length === 0 ? (
        <EmptyState>No Managers in the user roster yet.</EmptyState>
      ) : (
        data.teams.map((t, i) => <TeamSection key={t.team} team={t} index={i} />)
      )}

      <p className="text-[11px] leading-relaxed text-slate-400">
        Proposed counts exclude Student Reject and COI Detected — those never reached quality check. Teams come from the
        user roster&apos;s team field; editors with no team appear under Unassigned. In progress covers{" "}
        {preprintStatusLabel("To Pitch(Editorial)")}, {preprintStatusLabel("ACTIVE")}, {preprintStatusLabel("ON HOLD")} and{" "}
        {preprintStatusLabel("DORMANT")}.
      </p>

      <div className="flex justify-end pt-1">
        <a href={PUBLIC_SITE_HREF} target="_blank" rel="noopener" className="text-xs font-medium text-brand hover:underline">
          See the public RR\ID site ↗
        </a>
      </div>
    </div>
  );
}
